/**
 * Atmosphere — fresnel-rim halo rendered as a slightly larger back-facing sphere
 * behind the Globe. Additive blending, depth write off, so it composites cleanly
 * over the starfield and never z-fights with the surface.
 */

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  BackSide,
  Color,
  type Mesh,
  ShaderMaterial,
  SphereGeometry,
} from 'three';

export interface AtmosphereProps {
  /** Globe radius the atmosphere wraps. */
  readonly radius?: number;
  /** Halo thickness as a multiplier on radius. Default 1.06. */
  readonly scale?: number;
  /** Halo color. Default cyan. */
  readonly color?: string | number;
  /** Rim intensity multiplier. Default 1.4. */
  readonly intensity?: number;
}

const VERT = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vNormal = normalize(mat3(modelMatrix) * normal);
    vViewDir = normalize(cameraPosition - worldPos.xyz);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  uniform vec3 uColor;
  uniform float uIntensity;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    float rim = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), 3.0);
    vec3 col = uColor * rim * uIntensity;
    gl_FragColor = vec4(col, rim);
  }
`;

export function Atmosphere({
  radius = 1,
  scale = 1.06,
  color = 0x4ad7ff,
  intensity = 1.4,
}: AtmosphereProps): JSX.Element {
  const meshRef = useRef<Mesh>(null);

  const geometry = useMemo(() => new SphereGeometry(radius * scale, 96, 64), [radius, scale]);

  const material = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          uColor: { value: new Color(color) },
          uIntensity: { value: intensity },
        },
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
        side: BackSide,
      }),
    [color, intensity],
  );

  useFrame(() => {
    // Reserved for breathing intensity tied to telemetry (e.g. global RTT spike).
  });

  return <mesh ref={meshRef} geometry={geometry} material={material} />;
}
